import React from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

/**
 * PdfLangButton
 *
 * Reemplaza un botón "Ver PDF" tradicional. Muestra el mismo trigger (con el
 * icono / etiqueta que el llamante quiera), pero al pulsarlo abre un pequeño
 * menú con dos opciones:
 *   🇪🇸 Español
 *   🇬🇧 English
 *
 * Al elegir un idioma, llama a `onOpen({ url, filename, title })` con la URL
 * apropiada (añadiendo `?lang=es` o `?lang=en` correctamente sobre la URL base).
 *
 * Props:
 *   - baseUrl: URL base del endpoint PDF (sin query). Ej. `${BACKEND_URL}/api/budgets/<id>/pdf`.
 *   - filename: nombre de fichero sugerido.
 *   - title: título que verá el usuario en el diálogo de preview.
 *   - onOpen: callback `({ url, filename, title }) => void` para abrir el PDF (típicamente `setPreview`).
 *   - children: trigger visual (botón con icono). Si no se pasa, se usa un botón por defecto.
 *   - className, dataTestid: opcional para tests/estilos.
 *   - menuLabel: encabezado del menú (default: "Idioma").
 */
export default function PdfLangButton({
  baseUrl,
  filename,
  title,
  onOpen,
  children,
  menuLabel = 'Idioma',
  dataTestid,
}) {
  const buildUrl = (lang) => {
    const sep = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${sep}lang=${lang}`;
  };

  const openInLang = (lang) => {
    onOpen?.({
      url: buildUrl(lang),
      filename,
      title,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild data-testid={dataTestid}>
        {children || (
          <Button size="sm" variant="outline" title="Ver PDF">
            PDF
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {menuLabel}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => openInLang('es')}
          data-testid={dataTestid ? `${dataTestid}-es` : undefined}
          className="cursor-pointer"
        >
          <span className="mr-2 text-base leading-none">🇪🇸</span>
          <span>Español</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => openInLang('en')}
          data-testid={dataTestid ? `${dataTestid}-en` : undefined}
          className="cursor-pointer"
        >
          <span className="mr-2 text-base leading-none">🇬🇧</span>
          <span>English</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
